import { cn } from '@commander/utils';

export interface TrialButtonProps {
  /** Whether the current account is a trial. Renders nothing when false. */
  isTrial: boolean;
  /** ISO timestamp when the trial expires, or null. */
  trialEndTs: string | null;
  /** Invoked when the button is clicked — route to the upgrade flow. */
  onUpgrade: () => void;
  className?: string;
}

const MS_PER_HOUR = 1000 * 60 * 60;
const MS_PER_DAY = MS_PER_HOUR * 24;

/**
 * Coarse "time left" label from the live expiry: whole days when at least a day
 * remains, otherwise whole hours (min 1h) — so a short, hours-long trial reads
 * "Trial · 5h left" instead of rounding up to a misleading "1d left".
 */
function formatRemaining(msLeft: number): { short: string; long: string } {
  if (msLeft >= MS_PER_DAY) {
    const days = Math.ceil(msLeft / MS_PER_DAY);
    return { short: `${days}d left`, long: `${days} days left` };
  }
  const hours = Math.max(1, Math.ceil(msLeft / MS_PER_HOUR));
  return { short: `${hours}h left`, long: `${hours} hours left` };
}

/**
 * Compact, clickable trial-status button (a pill). While the trial is active it
 * shows the time remaining ("Trial · 5d left", or "Trial · 5h left" under a
 * day); once expired it flips to an urgent "Upgrade now". The whole pill is
 * clickable at any time. Presentational only — pass `isTrial`/`trialEndTs` from
 * `useTrialStatus()` and wire `onUpgrade` to the upgrade flow. Renders nothing
 * for non-trial accounts.
 */
export function TrialButton({ isTrial, trialEndTs, onUpgrade, className }: TrialButtonProps) {
  if (!isTrial) {
    return null;
  }

  const end = trialEndTs ? new Date(trialEndTs) : null;
  const msLeft = end ? end.getTime() - Date.now() : null;
  // A trial with no end date is active (unknown remaining), not expired.
  const expired = msLeft != null && msLeft <= 0;
  const remaining = msLeft != null && msLeft > 0 ? formatRemaining(msLeft) : null;

  return (
    <button
      type="button"
      onClick={onUpgrade}
      aria-label={
        expired
          ? 'Trial ended — upgrade now'
          : remaining != null
            ? `Trial: ${remaining.long}. Upgrade`
            : 'Trial. Upgrade'
      }
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition-colors',
        expired
          ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
          : 'border border-primary/20 bg-primary/10 text-primary hover:bg-primary/20',
        className
      )}
    >
      {expired ? 'Upgrade now' : remaining != null ? `Trial · ${remaining.short}` : 'Trial'}
    </button>
  );
}
